import React, { useState } from 'react'
import images from '../../Utils/images'
import { useNavigate } from 'react-router-dom'
import colors from '../../Utils/colors';
import { FormControl, IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography, InputAdornment, Button as MuiButton, InputLabel, Box } from '@mui/material';
import CheerioButton from '../../Components/CheerioButton';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from 'react-redux';
import { createTemplateRCS, V2WorkflowS3Link } from '../../Services';
import { toast } from 'react-toastify';
import MessagePreview from './MessagePreview';
import CloseIcon from "@mui/icons-material/Close";
import { eventsend } from '../../Config/analyticsFunctions';
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const HeaderWithInfo = ({ label, info }) => (
  <div className="d-flex align-items-center mb-1 w-100">
    <label className="fw-bold me-1">{label}</label>
    <Tooltip title={info} placement="right">
      <IconButton size="small" sx={{ p: 0 }}>
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </div>
);

// --- Helpers: validation + base64 + mime checks
const MB = 1024 * 1024;
const ALLOWED_IMAGES = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
const ALLOWED_VIDEOS = [
  "video/mp4",
  "video/m4v",
  "video/mpeg",
  "video/webm",
  "video/h263",
  "video/m4p",
];

const isVideoUrl = (url = "") => /\.(mp4|m4v|mpeg|webm|h263|m4p)(\?|#|$)/i.test(url);
// Add these helper text constants at the top of your component
const HELPER_TEXT = {
  imageSpecs: {
    richcard: "Recommended: 1440×480px (3:1 aspect ratio), Max 2MB. Supports JPEG, JPG, PNG, GIF",
    carousel: "Recommended: 1440×720px (5:2 aspect ratio), Max 1MB. Supports JPEG, JPG, PNG, GIF"
  },
  variables: "Use [1], [2], [3]... for dynamic text content and [url1], [url2]... for dynamic URLs. These will be replaced with actual values when sending.",
  orientationOptions: {
    VERTICAL: "Card displays vertically with image on top",
    HORIZONTAL: "Card displays horizontally with image on left"
  },
  heightOptions: {
    SHORT: "Compact card height for brief content",
    MEDIUM: "Standard card height for moderate content",
    TALL: "Extended card height for detailed content"
  },
  widthOptions: {
    SMALL: "Narrow carousel cards",
    MEDIUM: "Standard carousel card width",
    LARGE: "Wide carousel cards for detailed content"
  }
};
const validateRCSMedia = (file, { isCarousel }) => {
  const t = file.type?.toLowerCase() || "";
  const isImage = ALLOWED_IMAGES.includes(t);
  const isVideo = ALLOWED_VIDEOS.includes(t);

  if (!isImage && !isVideo) {
    return { ok: false, msg: "Unsupported file type. Use JPEG/JPG/PNG/GIF for images or MP4/M4V/MPEG/WEBM/H263/M4P for videos." };
  }

  // size limits
  const max = isImage
    ? isCarousel ? 1 * MB : 2 * MB
    : isCarousel ? 5 * MB : 10 * MB;

  if (file.size > max) {
    return {
      ok: false,
      msg: `File too large. Max size is ${isImage ? (isCarousel ? "1MB" : "2MB") : (isCarousel ? "5MB" : "10MB")}.`,
    };
  }
  return { ok: true };
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const dynamicTokenUrl = (n) =>
  `https://cheerio-preprod.s3.ap-south-1.amazonaws.com/[img${n}]`;


// --- Reusable MediaPicker
// Add these constants at the top with other helper text
const MEDIA_TYPE_OPTIONS = {
  image: "Image",
  video: "Video"
};

// Update the MediaPicker component
const MediaPicker = ({
  mode = "richcard",
  cardIndex = 0,
  mediaUrl = "",
  thumbnailUrl = "",           // Add thumbnail URL prop
  mediaMode = "static",
  mediaType = "image",         // Add media type prop
  onMediaUrlChange,
  onThumbnailUrlChange,        // Add thumbnail change handler
  onMediaModeChange,
  onMediaTypeChange,           // Add media type change handler
  authtoken,
}) => {
  const isCarousel = mode === "carousel";
  const inputRef = React.useRef(null);
  const thumbnailInputRef = React.useRef(null);  // Add thumbnail input ref
  const dropRef = React.useRef(null);

  const setDynamic = () => {
    const n = isCarousel ? cardIndex + 1 : 1;
    onMediaModeChange?.("dynamic");
    onMediaUrlChange?.(dynamicTokenUrl(n));
    // Clear thumbnail when switching to dynamic
    onThumbnailUrlChange?.("");
  };

  const setStatic = () => {
    onMediaModeChange?.("static");
    onMediaUrlChange?.("");
    onThumbnailUrlChange?.("");
  };

  const onSelectMode = (v) => (v === "dynamic" ? setDynamic() : setStatic());

  const handleFiles = async (files, isForThumbnail = false) => {
    if (!files?.length) return;
    const file = files[0];

    // For thumbnail, only allow images
    if (isForThumbnail) {
      const isImage = ALLOWED_IMAGES.includes(file.type?.toLowerCase());
      if (!isImage) {
        toast.error("Thumbnail must be an image (JPEG, JPG, PNG, GIF)", { position: "top-center" });
        return;
      }
      // Size limit for thumbnail (smaller)
      if (file.size > 1 * MB) {
        toast.error("Thumbnail size must be less than 1MB", { position: "top-center" });
        return;
      }
    } else {
      // Validate based on selected media type
      const t = file.type?.toLowerCase() || "";
      const isImage = ALLOWED_IMAGES.includes(t);
      const isVideo = ALLOWED_VIDEOS.includes(t);

      if (mediaType === "image" && !isImage) {
        toast.error("Please select a valid image file", { position: "top-center" });
        return;
      }
      if (mediaType === "video" && !isVideo) {
        toast.error("Please select a valid video file", { position: "top-center" });
        return;
      }

      const valid = validateRCSMedia(file, { isCarousel });
      if (!valid.ok) {
        toast.error(valid.msg, { position: "top-center" });
        return;
      }
    }

    const loadingToastId = toast.loading(
      `Uploading ${isForThumbnail ? 'thumbnail' : 'media'}...`,
      { position: "top-center" }
    );

    try {
      const dataUrl = await fileToBase64(file);
      const payload = {
        image: dataUrl,
        type: file?.type?.split("/")[0],
        format: file?.type?.split("/")[1],
      };

      const res = await V2WorkflowS3Link(payload, authtoken);
      const url = res?.data?.url;
      if (!url) throw new Error("S3 link not returned");

      if (isForThumbnail) {
        onThumbnailUrlChange?.(url);
      } else {
        onMediaUrlChange?.(url);
      }

      toast.update(loadingToastId, {
        render: `${isForThumbnail ? 'Thumbnail' : 'Media'} uploaded successfully!`,
        type: "success",
        isLoading: false,
        position: "top-right",
        autoClose: 1500,
      });
    } catch (e) {
      console.error(e);
      toast.update(loadingToastId, {
        render: "Upload failed. Please try again.",
        type: "error",
        isLoading: false,
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const onInputChange = (e) => handleFiles(e.target.files, false);
  const onThumbnailInputChange = (e) => handleFiles(e.target.files, true);

  // Update drag & drop to respect media type
  React.useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

    const onDrop = (e) => {
      stop(e);
      if (mediaMode === "dynamic") return;
      const dt = e.dataTransfer;
      if (dt?.files?.length) handleFiles(dt.files, false);
    };

    el.addEventListener("dragenter", stop);
    el.addEventListener("dragover", stop);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragenter", stop);
      el.removeEventListener("dragover", stop);
      el.removeEventListener("drop", onDrop);
    };
  }, [mediaMode, mediaType]);

  const removeMedia = () => {
    onMediaUrlChange?.("");
    onThumbnailUrlChange?.("");
    if (inputRef.current) inputRef.current.value = "";
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
  };

  const removeThumbnail = () => {
    onThumbnailUrlChange?.("");
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
  };

  const showDynamicPlaceholder = mediaMode === "dynamic" && /\[img\d+\]/i.test(mediaUrl);
  const acceptedTypes = mediaType === "image"
    ? ALLOWED_IMAGES.join(",")
    : mediaType === "video"
      ? ALLOWED_VIDEOS.join(",")
      : [...ALLOWED_IMAGES, ...ALLOWED_VIDEOS].join(",");

  return (
    <Box sx={{ mb: 2, width: '100%' }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <div>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Header</Typography>
          <Typography variant="caption" sx={{ color: "#777" }}>Optional — add image or video</Typography>
          <Typography variant="caption" sx={{ color: "#555", display: 'block', mt: 0.5 }}>
            {HELPER_TEXT.imageSpecs[mode]}
          </Typography>
        </div>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {/* Media Type Selector */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id={`media-type-${mode}-${cardIndex}`}>Type</InputLabel>
            <Select
              labelId={`media-type-${mode}-${cardIndex}`}
              label="Type"
              value={mediaType}
              onChange={(e) => {
                console.log("Media type changed to 2:", e.target.value);
                onMediaTypeChange?.(e.target.value);
                // Clear media and thumbnail when changing type
                onMediaUrlChange?.("");
                onThumbnailUrlChange?.("");
                if (inputRef.current) inputRef.current.value = "";
                if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
              }}
              disabled={mediaMode === "dynamic"}
            >
              <MenuItem value="image">Image</MenuItem>
              <MenuItem value="video">Video</MenuItem>
            </Select>
          </FormControl>

          {/* Mode Selector */}
          {/* <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id={`media-mode-${mode}-${cardIndex}`}>Mode</InputLabel>
            <Select
              labelId={`media-mode-${mode}-${cardIndex}`}
              label="Mode"
              value={mediaMode}
              onChange={(e) => onSelectMode(e.target.value)}
            >
              <MenuItem value="static">Static</MenuItem>
              <MenuItem value="dynamic">Dynamic</MenuItem>
            </Select>
          </FormControl> */}
        </Box>
      </Box>

      {/* Upload Box */}
      <Box
        ref={dropRef}
        sx={{
          position: "relative",
          border: "2px dashed #c9c9c9",
          borderRadius: 2,
          height: 180,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: mediaMode === "dynamic" ? "#fafafa" : "#fff",
          opacity: mediaMode === "dynamic" ? 0.6 : 1,
          cursor: mediaMode === "dynamic" ? "not-allowed" : "pointer",
          mb: 1.5,
          overflow: "hidden",
        }}
        onClick={() => mediaMode === "static" && inputRef.current?.click()}
        title={
          mediaMode === "static"
            ? `Click or drag & drop to upload ${mediaType}`
            : "Dynamic mode disables uploads"
        }
      >
        {/* preview / placeholder */}
        {mediaUrl ? (
          <>
            {showDynamicPlaceholder ? (
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Dynamic media token
                </Typography>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  {mediaUrl}
                </Typography>
              </Box>
            ) : mediaType === "video" || isVideoUrl(mediaUrl) ? (
              <video
                src={mediaUrl}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                muted
                controls
                playsInline
              />
            ) : (
              <img
                src={mediaUrl}
                alt="media"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}

            {/* remove */}
            {mediaMode === "static" && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); removeMedia(); }}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  bgcolor: "#fff",
                  "&:hover": { bgcolor: "#f3f3f3" },
                  border: "1px solid #ddd",
                }}
                aria-label="Remove"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: "center", px: 2 }}>
            <CloudUploadIcon sx={{ fontSize: 36, color: "#c2c2c2" }} />
            <Typography variant="body2" sx={{ color: "#555" }}>
              Upload {mediaType} here
            </Typography>
            <Typography variant="caption" sx={{ color: "#888" }}>
              {mediaType === "image"
                ? (isCarousel ? "Images ≤ 1MB" : "Images ≤ 2MB")
                : (isCarousel ? "Videos ≤ 5MB" : "Videos ≤ 10MB")
              }
            </Typography>
          </Box>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={acceptedTypes}
          style={{ display: "none" }}
          onChange={onInputChange}
          disabled={mediaMode === "dynamic"}
        />
      </Box>

      {/* Thumbnail Upload Section - Only show for videos in static mode */}
      {mediaType === "video" && mediaMode === "static" && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Video Thumbnail
            </Typography>
            <Typography variant="caption" sx={{ color: "#777" }}>
              Required for videos (Max 1MB)
            </Typography>
          </Box>

          <Box
            sx={{
              position: "relative",
              border: "2px dashed #c9c9c9",
              borderRadius: 2,
              height: 120,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#fff",
              cursor: "pointer",
              overflow: "hidden",
            }}
            onClick={() => thumbnailInputRef.current?.click()}
          >
            {thumbnailUrl ? (
              <>
                <img
                  src={thumbnailUrl}
                  alt="thumbnail"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); removeThumbnail(); }}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    bgcolor: "#fff",
                    "&:hover": { bgcolor: "#f3f3f3" },
                    border: "1px solid #ddd",
                  }}
                  aria-label="Remove thumbnail"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <Box sx={{ textAlign: "center", px: 2 }}>
                <CloudUploadIcon sx={{ fontSize: 24, color: "#c2c2c2" }} />
                <Typography variant="body2" sx={{ color: "#555" }}>
                  Upload thumbnail image
                </Typography>
                <Typography variant="caption" sx={{ color: "#888" }}>
                  JPEG, PNG, GIF ≤ 1MB
                </Typography>
              </Box>
            )}

            <input
              ref={thumbnailInputRef}
              type="file"
              accept={ALLOWED_IMAGES.join(",")}
              style={{ display: "none" }}
              onChange={onThumbnailInputChange}
            />
          </Box>

          {/* Thumbnail URL field */}
          <TextField
            size="small"
            label="Thumbnail URL"
            value={thumbnailUrl}
            onChange={(e) => onThumbnailUrlChange?.(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />
        </Box>
      )}

      {/* Media URL (always visible) */}
      <TextField
        size="small"
        label="Media URL"
        value={mediaUrl}
        onChange={(e) => onMediaUrlChange?.(e.target.value)}
        fullWidth
        InputProps={{
          readOnly: mediaMode === "dynamic",
        }}
      />
    </Box>
  );
};


// ===== AttrField: '+ Attr' inserts next token — [n] for body, [urln] for URL =====
const AttrField = ({
  tokenType = 'body',          // 'body' | 'url'
  value = '',
  onChange,
  getNextAttrToken,           // callback from parent: (tokenType) => string like "[1]" or "[url1]"
  size = 'small',
  label,
  placeholder,
  multiline = false,
  rows,
  fullWidth,
  style,
}) => {
  const inputRef = React.useRef(null);

  const insertNextToken = () => {
    if (!getNextAttrToken) return;

    // Always read the DOM live value
    const live = inputRef.current?.value ?? String(value);
    const tok = getNextAttrToken(tokenType, live);

    const el = inputRef.current;
    const cur = live;
    const start = el?.selectionStart ?? cur.length;
    const end = el?.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + tok + cur.slice(end);
    onChange?.({ target: { value: next } });

    setTimeout(() => {
      if (inputRef.current) {
        const pos = start + tok.length;
        inputRef.current.focus();
        inputRef.current.setSelectionRange?.(pos, pos);
      }
    }, 0);
  };

  return (
    <TextField
      size={size}
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      multiline={multiline}
      rows={rows}
      fullWidth={fullWidth}
      inputRef={inputRef}
      style={style}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <MuiButton
              variant="outlined"
              size="small"
              onClick={insertNextToken}
              title="Insert next attribute token"
            >
              + Attr
            </MuiButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

const UrlField = ({
  value = '',
  onChange,
  getNextAttrToken,
  size = 'small',
  label,
  placeholder,
  fullWidth,
  style,
}) => {
  const inputRef = React.useRef(null);

  // Determine if current value is dynamic (contains [url...] pattern)
  const isDynamic = /^\[url\d+\]$/.test(value.replace('https://1kx.in/', ''));
  const [mode, setMode] = React.useState(isDynamic ? 'dynamic' : 'static');

  const handleModeChange = (newMode) => {
    setMode(newMode);

    if (newMode === 'dynamic') {
      // Get next URL token and format it
      const token = getNextAttrToken('url', '');
      const dynamicUrl = `https://1kx.in/${token}`;
      onChange?.({ target: { value: dynamicUrl } });
    } else {
      // Clear the field for static input
      onChange?.({ target: { value: '' } });
    }

    // Focus the input after mode change
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;

    if (mode === 'dynamic') {
      // In dynamic mode, extract the token part and reformat
      const tokenMatch = inputValue.match(/\[url\d+\]/);
      if (tokenMatch) {
        const formattedValue = `https://1kx.in/${tokenMatch[0]}`;
        onChange?.({ target: { value: formattedValue } });
      } else {
        // If user removes the token, keep the base URL
        onChange?.({ target: { value: 'https://1kx.in/' } });
      }
    } else {
      // Static mode - pass through as is
      onChange?.(e);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
      <TextField
        size={size}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        fullWidth={fullWidth}
        inputRef={inputRef}
        style={{ ...style, flex: 1 }}
        InputProps={{
          readOnly: mode === 'dynamic',
        }}
      />

      <FormControl size={size} sx={{ minWidth: 100 }}>
        <InputLabel>Mode</InputLabel>
        <Select
          value={mode}
          label="Mode"
          onChange={(e) => handleModeChange(e.target.value)}
        >
          <MenuItem value="static">Static</MenuItem>
          <MenuItem value="dynamic">Dynamic</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

const TYPE_LABELS = {
  reply: "Text Reply",
  dialer_action: "Call",       // keep if backend expects "dialer_action"
  url_action: "URL/Web Link",
};

const blankButton = (type) => {
  switch (type) {
    case "reply":
      return { suggestionType: "reply", displayText: "", postback: "" };
    case "dialer_action":
      return { suggestionType: "dialer_action", phoneNumber: "", displayText: "", postback: "" };
    case "url_action":
      return { suggestionType: "url_action", url: "", displayText: "", postback: "" };
    default:
      return null;
  }
};

const underscore = (s = "") =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

/** --- NEW: helpers for safe immutable updates on nested paths --- */
const put = (obj, path, value) => {
  const segs = Array.isArray(path) ? path : String(path).split(".");
  const next = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = next;
  for (let i = 0; i < segs.length - 1; i++) {
    const k = segs[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...(cur[k] || {}) };
    cur = cur[k];
  }
  cur[segs[segs.length - 1]] = value;
  return next;
};

// === extract placeholders from the whole template ===
// Matches: [1], [2], ... and [url1], [url2], ... (ignores [img1] etc)
const PLACEHOLDER_REGEX = /\[(url)?(\d+)\]/gi;

const collectAllStrings = (node, out) => {
  if (node == null) return;
  if (typeof node === 'string') { out.push(node); return; }
  if (Array.isArray(node)) { node.forEach(n => collectAllStrings(n, out)); return; }
  if (typeof node === 'object') {
    Object.values(node).forEach(v => collectAllStrings(v, out));
  }
};

const extractTemplateParams = (form) => {
  const texts = [];
  collectAllStrings(form, texts);

  const names = [];
  const seen = new Set();

  for (const t of texts) {
    let m;
    while ((m = PLACEHOLDER_REGEX.exec(t)) !== null) {
      const isUrl = !!m[1];
      const num = m[2];
      const name = isUrl ? `url${num}` : `${num}`;
      if (!seen.has(name)) {
        seen.add(name);
        names.push(name);
      }
    }
  }
  return {
    templatePlaceHolderCount: names.length,
    templateParamNames: names,
  };
};

const RCSTemplateCreation = () => {
  const authtoken = useSelector((state) => state.main.auth_token);
  const localdata = useSelector((state) => state.main.localdata);
  const navigate = useNavigate();

  /** Form now supports all three types */
  const [form, setForm] = useState({
    // shared
    type: "",
    textMessage: "",
    suggestions: [],

    // richcard
    orientation: "VERTICAL",
    height: "SHORT",
    standaloneCard: {
      mediaMode: "static",
      mediaType: "image",      // Add this
      cardTitle: "",
      cardDescription: "",
      mediaUrl: "",
      thumbnailUrl: "",        // Add this
      suggestions: [],
    },

    // carousel
    width: "MEDIUM",
    carouselCard: [
      {
        mediaMode: "static",
        mediaType: "image",    // Make sure this is included
        cardTitle: "",
        cardDescription: "",
        mediaUrl: "",
        thumbnailUrl: "",      // Make sure this is included
        suggestions: [],
      },
      {
        mediaMode: "static",
        mediaType: "image",    // Make sure this is included
        cardTitle: "",
        cardDescription: "",
        mediaUrl: "",
        thumbnailUrl: "",      // Make sure this is included
        suggestions: [],
      },
    ],
  });

  // template-level attribute counter (shared everywhere)
  const [fields, setFields] = useState({
    message: '',
    buttons: [
      { text: '', url: '' },
    ],
  });

  const TOKEN_RE = {
    body: /\[(\d+)\]/g,
    url: /\[url(\d+)\]/g,
    img: /\[img(\d+)\]/g,
  };

  function nextIndexFromText(text, type) {
    const re = TOKEN_RE[type];
    if (!re) return 1;
    const used = new Set();
    let m;
    while ((m = re.exec(text))) used.add(Number(m[1]));
    let i = 1;
    while (used.has(i)) i++;
    return i;
  }

  function makeToken(type, idx) {
    if (type === 'url') return `[url${idx}]`;
    if (type === 'img') return `[img${idx}]`;
    return `[${idx}]`; // body
  }

  function collectAllText(form) {
    const parts = [];

    // shared
    parts.push(form.textMessage);

    if (Array.isArray(form.suggestions)) {
      for (const s of form.suggestions) {
        if (typeof s === "string") {
          parts.push(s);
        } else if (s && typeof s === "object") {
          parts.push(s.displayText);
          parts.push(s.url);
          parts.push(s.postback);
          parts.push(s.phoneNumber);
        }
      }
    }

    // standalone rich card
    if (form.standaloneCard) {
      parts.push(form.standaloneCard.cardTitle);
      parts.push(form.standaloneCard.cardDescription);
      parts.push(form.standaloneCard.mediaUrl);

      if (Array.isArray(form.standaloneCard.suggestions)) {
        for (const s of form.standaloneCard.suggestions) {
          if (typeof s === "string") {
            parts.push(s);
          } else if (s && typeof s === "object") {
            parts.push(s.displayText);
            parts.push(s.url);
            parts.push(s.postback);
            parts.push(s.phoneNumber);
          }
        }
      }
    }

    // carousel cards
    if (Array.isArray(form.carouselCard)) {
      for (const c of form.carouselCard) {
        parts.push(c.cardTitle);
        parts.push(c.cardDescription);
        parts.push(c.mediaUrl);

        if (Array.isArray(c.suggestions)) {
          for (const s of c.suggestions) {
            if (typeof s === "string") {
              parts.push(s);
            } else if (s && typeof s === "object") {
              parts.push(s.displayText);
              parts.push(s.url);
              parts.push(s.postback);
              parts.push(s.phoneNumber);
            }
          }
        }
      }
    }

    return parts.filter(Boolean).join("\n");
  }


  const allTextRef = React.useRef('');
  React.useEffect(() => {
    allTextRef.current = collectAllText(form);
  }, [form, collectAllText]);

  const getNextAttrToken = React.useCallback(
    (tokenType = 'body', localFieldText = '') => {
      // collect from latest state
      const allText = collectAllText(form);

      // also merge the live field text (may not yet be in form)
      const combined = `${allText}\n${localFieldText}`;

      const idx = nextIndexFromText(combined, tokenType);
      return makeToken(tokenType, idx);
    },
    [form]
  );

  const [picker, setPicker] = useState(""); // for adding buttons
  const [pickerStandalone, setPickerStandalone] = useState(""); // richcard button add
  const [carouselPicker, setCarouselPicker] = useState({}); // { [cardIdx]: 'url_action' | ... }

  const [templateName, setTemplateName] = useState("");


  /** --- generic change helpers --- */
  const handleChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const update = (path, value) => {
    setForm(prev => {
      const segs = Array.isArray(path) ? path : String(path).split(".");
      const next = Array.isArray(prev) ? [...prev] : { ...prev };

      let cur = next;
      for (let i = 0; i < segs.length - 1; i++) {
        const k = segs[i];
        cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...(cur[k] || {}) };
        cur = cur[k];
      }

      cur[segs[segs.length - 1]] = value;

      console.log("✅ update applied:", path, value); // debug
      return next;
    });
  };

  /** --- TEXT buttons (top-level suggestions) --- */
  const addSuggestion = (type) => {
    const obj = blankButton(type);
    if (!obj) return;
    setForm((prev) => ({ ...prev, suggestions: [...(prev.suggestions || []), obj] }));
  };
  const removeSuggestion = (idx) =>
    setForm((prev) => ({ ...prev, suggestions: prev.suggestions.filter((_, i) => i !== idx) }));
  const updateSuggestion = (idx, patch) =>
    setForm((prev) => {
      const next = [...prev.suggestions];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, suggestions: next };
    });

  const onPickChange = (e) => {
    const type = e.target.value;
    setPicker(type);
    addSuggestion(type);
    setTimeout(() => setPicker(""), 0);
  };

  /** --- RICH CARD: standaloneCard buttons --- */
  const addStandaloneButton = (type) => {
    const obj = blankButton(type);
    if (!obj) return;
    update("standaloneCard.suggestions", [...(form.standaloneCard?.suggestions || []), obj]);
  };
  const removeStandaloneButton = (idx) => {
    const next = [...(form.standaloneCard?.suggestions || [])].filter((_, i) => i !== idx);
    update("standaloneCard.suggestions", next);
  };
  const updateStandaloneButton = (idx, patch) => {
    const next = [...(form.standaloneCard?.suggestions || [])];
    next[idx] = { ...next[idx], ...patch };
    update("standaloneCard.suggestions", next);
  };
  const onStandalonePick = (e) => {
    const t = e.target.value;
    setPickerStandalone(t);
    addStandaloneButton(t);
    setTimeout(() => setPickerStandalone(""), 0);
  };

  /** --- CAROUSEL: cards, each with fields + buttons --- */
  const addCarouselCard = () => {
    update("carouselCard", [
      ...form.carouselCard,
      {
        cardTitle: "",
        cardDescription: "",
        mediaUrl: "",
        thumbnailUrl: "",       // Add this
        suggestions: [],
        mediaMode: "static",
        mediaType: "image"      // Add this
      },
    ]);
  };
  const removeCarouselCard = (idx) => {
    if (form.carouselCard.length <= 2) return; // at least two cards
    const next = form.carouselCard.filter((_, i) => i !== idx);
    update("carouselCard", next);
  };
  const updateCarouselCardField = (idx, key, value) => {
    const next = [...form.carouselCard];
    next[idx] = { ...next[idx], [key]: value };
    update("carouselCard", next);
  };

  const addCarouselButton = (cardIdx, type) => {
    const obj = blankButton(type);
    if (!obj) return;
    const next = [...form.carouselCard];
    next[cardIdx] = {
      ...next[cardIdx],
      suggestions: [...(next[cardIdx].suggestions || []), obj],
    };
    update("carouselCard", next);
  };
  const removeCarouselButton = (cardIdx, btnIdx) => {
    const next = [...form.carouselCard];
    next[cardIdx] = {
      ...next[cardIdx],
      suggestions: next[cardIdx].suggestions.filter((_, i) => i !== btnIdx),
    };
    update("carouselCard", next);
  };
  const updateCarouselButton = (cardIdx, btnIdx, patch) => {
    const next = [...form.carouselCard];
    const s = [...(next[cardIdx].suggestions || [])];
    s[btnIdx] = { ...s[btnIdx], ...patch };
    next[cardIdx] = { ...next[cardIdx], suggestions: s };
    update("carouselCard", next);
  };
  const onCarouselPick = (cardIdx) => (e) => {
    const t = e.target.value;
    setCarouselPicker((p) => ({ ...p, [cardIdx]: t }));
    addCarouselButton(cardIdx, t);
    setTimeout(() => setCarouselPicker((p) => ({ ...p, [cardIdx]: "" })), 0);
  };

  /** --- submit: include placeholder count + names --- */
  const submitTemplateAction = () => {
    const token = authtoken;

    // viTemplate comes straight from `form`, already shaped per `type`
    const mapBtn = (b) => ({
      suggestionType: b.suggestionType,
      text: b.displayText,
      postbackData: b.postback,
      url: b.url,
      phoneNumber: b.phoneNumber,
    });

    let viTemplate = { ...form };
    if (form.type === 'text') {
      viTemplate = { ...form, suggestions: (form.suggestions || []).map(mapBtn) };
    } else if (form.type === 'richcard') {
      viTemplate = {
        ...form,
        standaloneCard: {
          ...form.standaloneCard,
          suggestions: (form.standaloneCard?.suggestions || []).map(mapBtn),
        }
      };
    } else if (form.type === 'carousel') {
      viTemplate = {
        ...form,
        carouselCard: form.carouselCard.map(c => ({
          ...c,
          suggestions: (c.suggestions || []).map(mapBtn),
        })),
      };
    }

    // 🔥 derive params from the *original* form (so we see raw placeholders everywhere)
    const { templatePlaceHolderCount, templateParamNames } = extractTemplateParams(form);

    const data = {
      esmeAddr: localdata?.rcsUserDetails?.esmeAddr,
      botId: localdata?.rcsUserDetails?.botId,
      templateName,
      templatePlaceHolderCount,
      templateParamNames,
      viTemplate,                     // send normalized template here
      templateCategory: "PROMOTIONAL",
    };

    createTemplateRCS(token, data).then((res) => {
      if (res?.flag) {
         eventsend('template_creation_success', {
        template_name: templateName,
        template_type: form.type,
        timestamp: new Date().toISOString(),
        account_id: localdata?._id,
      });
        toast.success('Template created successfully!', { position: 'top-right', autoClose: true, closeButton: true })
        navigate('/creator/templates');
      } else {
        toast.error(res?.message ? res?.message : 'Template creation failed, please try again later', { position: 'top-right', autoClose: true, closeButton: true })
      }
    });
  };

  /** --- UI blocks --- */
  const renderTextButtonsEditor = () => (
    <div className='Column justify-content-start align-items-start w-100' style={{ padding: 20, marginBlock: 10, borderRadius: 16, border: `1px solid ${colors.borderwhite}`, backgroundColor: colors.white01 }}>
      <Stack spacing={1.5} className='w-100 align-items-start '>
        <HeaderWithInfo label="Button" info="Optional" />
        <p className='w-100 mb-2' style={{ color: colors.greys04 }}>
          Create buttons that let your customers either reply or take action
        </p>

        <FormControl size="small" sx={{ width: 200 }}>
          <Select
            size="small"
            displayEmpty
            value={picker}
            onChange={onPickChange}
            renderValue={(v) => v ? TYPE_LABELS[v] : <span style={{ color: "#9e9e9e" }}>Add Button</span>}
            sx={{ '& .MuiSelect-select': { py: 1 } }}
          >
            <MenuItem value="reply">{TYPE_LABELS.reply}</MenuItem>
            <MenuItem value="dialer_action">{TYPE_LABELS.dialer_action}</MenuItem>
            <MenuItem value="url_action">{TYPE_LABELS.url_action}</MenuItem>
          </Select>
        </FormControl>

        <Stack spacing={2} style={{ width: '100%' }}>
          {(form.suggestions || []).map((s, idx) => {
            const type = s.suggestionType;
            return (
              <Stack key={idx} direction="column" spacing={1} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1, mb: 4, width: '100%' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" style={{ borderBottom: `1px solid ${colors.borderwhite}`, paddingBlockEnd: 10 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{TYPE_LABELS[type] || type}</Typography>
                  <IconButton size="small" onClick={() => removeSuggestion(idx)}><DeleteIcon fontSize="small" /></IconButton>
                </Stack>

                {type === "reply" && (
                  <div className='w-100 py-2 Row' style={{ columnGap: 12 }}>
                    <TextField
                      size="small"
                      style={{ width: '50%' }}
                      label="Button Text"
                      placeholder="Click to Win"
                      value={s.displayText || ""}
                      onChange={(e) => {
                        const displayText = e.target.value;
                        updateSuggestion(idx, { displayText, postback: underscore(displayText) });
                      }}
                    />
                  </div>
                )}

                {type === "dialer_action" && (
                  <div className='w-100 py-2 Row' style={{ columnGap: 12 }}>
                    <TextField
                      size="small"
                      style={{ width: '50%' }}
                      label="Button Text"
                      placeholder="Click to Win"
                      value={s.displayText || ""}
                      onChange={(e) => {
                        const displayText = e.target.value;
                        updateSuggestion(idx, { displayText, postback: underscore(displayText) });
                      }}
                    />
                    <TextField
                      size="small" style={{ width: '50%' }}
                      label="Phone Number" placeholder="+919876543210"
                      value={s.phoneNumber || ""}
                      onChange={(e) => updateSuggestion(idx, { phoneNumber: e.target.value })}
                    />
                  </div>
                )}

                {type === "url_action" && (
                  <div className='w-100 py-2 Row' style={{ columnGap: 12 }}>
                    <TextField
                      size="small"
                      style={{ width: '50%' }}
                      label="Button Text"
                      placeholder="Click to Win"
                      value={s.displayText || ""}
                      onChange={(e) => {
                        const displayText = e.target.value;
                        updateSuggestion(idx, { displayText, postback: underscore(displayText) });
                      }}
                    />
                    <UrlField
                      style={{ width: '50%' }}
                      size="small"
                      label="Button Link"
                      placeholder="https://example.com"
                      value={s.url || ""}
                      getNextAttrToken={getNextAttrToken}
                      onChange={(e) => updateSuggestion(idx, { url: e.target.value })}
                    />
                  </div>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </div>
  );

  const renderRichCardEditor = () => (
    <div className='Column justify-content-start align-items-start w-100' style={{ padding: 20, marginBlock: 10, borderRadius: 16, border: `1px solid ${colors.borderwhite}`, backgroundColor: colors.white01 }}>
      <HeaderWithInfo label="Rich Card" info="Configure a single rich card" />

      <div className='Column w-100' style={{ rowGap: 12 }}>
        <MediaPicker
          mode="richcard"
          cardIndex={0}
          mediaUrl={form.standaloneCard.mediaUrl}
          thumbnailUrl={form.standaloneCard.thumbnailUrl}
          mediaMode={form.standaloneCard.mediaMode}
          mediaType={form.standaloneCard.mediaType}
          onMediaUrlChange={(url) => update("standaloneCard.mediaUrl", url)}
          onThumbnailUrlChange={(url) => update("standaloneCard.thumbnailUrl", url)}
          onMediaModeChange={(m) => update("standaloneCard.mediaMode", m)}
          onMediaTypeChange={(t) => update("standaloneCard.mediaType", t)}
          authtoken={authtoken}
        />
        <AttrField
          tokenType='body'
          size="small" label="Card Title" placeholder="🚀 Supercharge Your Marketing"
          value={form.standaloneCard.cardTitle}
          getNextAttrToken={getNextAttrToken}
          onChange={(e) => update("standaloneCard.cardTitle", e.target.value)}
          fullWidth
        />
        <AttrField
          tokenType='body'
          size="small" label="Card Description" placeholder="Run campaigns on WhatsApp, Email & SMS..."
          value={form.standaloneCard.cardDescription}
          getNextAttrToken={getNextAttrToken}
          onChange={(e) => update("standaloneCard.cardDescription", e.target.value)}
          fullWidth
          multiline rows={3}
        />
      </div>

      {/* Rich Card Buttons */}
      <div className='Column align-items-start w-100' style={{ marginTop: 16 }}>
        <HeaderWithInfo label="Buttons" info="Optional" />
        <FormControl size="small" sx={{ width: 200 }}>
          <Select
            size="small"
            displayEmpty
            value={pickerStandalone}
            onChange={onStandalonePick}
            renderValue={(v) => v ? TYPE_LABELS[v] : <span style={{ color: "#9e9e9e" }}>Add Button</span>}
            sx={{ '& .MuiSelect-select': { py: 1 } }}
          >
            <MenuItem value="reply">{TYPE_LABELS.reply}</MenuItem>
            <MenuItem value="dialer_action">{TYPE_LABELS.dialer_action}</MenuItem>
            <MenuItem value="url_action">{TYPE_LABELS.url_action}</MenuItem>
          </Select>
        </FormControl>

        <Stack spacing={2} style={{ width: '100%', marginTop: 12 }}>
          {(form.standaloneCard.suggestions || []).map((s, idx) => {
            const type = s.suggestionType;
            return (
              <Stack key={idx} direction="column" spacing={1} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1, width: '100%' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" style={{ borderBottom: `1px solid ${colors.borderwhite}`, paddingBlockEnd: 10 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{TYPE_LABELS[type] || type}</Typography>
                  <IconButton size="small" onClick={() => removeStandaloneButton(idx)}><DeleteIcon fontSize="small" /></IconButton>
                </Stack>

                {type === "reply" && (
                  <div className='w-100 py-2 Row' style={{ columnGap: 12 }}>
                    <TextField
                      size="small"
                      style={{ width: '50%' }}
                      label="Button Text"
                      placeholder="Click to Win"
                      value={s.displayText || ""}
                      onChange={(e) => {
                        const displayText = e.target.value;
                        updateStandaloneButton(idx, { displayText, postback: underscore(displayText) }); // Changed this line
                      }}
                    />
                  </div>
                )}

                {type === "dialer_action" && (
                  <div className='w-100 py-2 Row' style={{ columnGap: 12 }}>
                    <TextField
                      size="small"
                      style={{ width: '50%' }}
                      label="Button Text"
                      placeholder="Click to Win"
                      value={s.displayText || ""}
                      onChange={(e) => {
                        const displayText = e.target.value;
                        updateStandaloneButton(idx, { displayText, postback: underscore(displayText) }); // Changed this line
                      }}
                    />
                    <TextField
                      size="small" style={{ width: '50%' }}
                      label="Phone Number" placeholder="+919876543210"
                      value={s.phoneNumber || ""}
                      onChange={(e) => updateStandaloneButton(idx, { phoneNumber: e.target.value })}
                    />
                  </div>
                )}

                {type === "url_action" && (
                  <div className='w-100 py-2 Row' style={{ columnGap: 12 }}>
                    <TextField
                      size="small"
                      style={{ width: '50%' }}
                      label="Button Text"
                      placeholder="Click to Win"
                      value={s.displayText || ""}
                      onChange={(e) => {
                        const displayText = e.target.value;
                        updateStandaloneButton(idx, { displayText, postback: underscore(displayText) }); // Changed this line
                      }}
                    />
                    <UrlField
                      style={{ width: '50%' }}
                      size="small" label="Button Link" placeholder="https://example.com"
                      value={s.url || ""}
                      getNextAttrToken={getNextAttrToken}
                      onChange={(e) => updateStandaloneButton(idx, { url: e.target.value })}
                    />
                  </div>
                )}
              </Stack>
            );
          })}
        </Stack>
      </div>
    </div>
  );

  const renderCarouselEditor = () => (
    <div className='Column justify-content-start align-items-start w-100' style={{ padding: 20, marginBlock: 10, borderRadius: 16, border: `1px solid ${colors.borderwhite}`, backgroundColor: colors.white01 }}>
      <HeaderWithInfo label="Carousel" info="Create at least two cards" />
      <div className='Row w-100' style={{ columnGap: 12, marginTop: 8, marginBottom: 16 }}>
        <CheerioButton
          btnText={'Add Card'}
          textStyle={{ color: colors.white01 }}
          borderStyle={{ border: `1px solid ${colors.primary03}` }}
          backColor={colors.primary03}
          onclick={addCarouselCard}
        />
      </div>

      <Stack spacing={2} className='w-100'>
        {form.carouselCard.map((card, cIdx) => (
          <div key={cIdx} className='Column w-100' style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16 }}>
            <div className='Row w-100' style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Card #{cIdx + 1}</Typography>
              <IconButton size="small" onClick={() => removeCarouselCard(cIdx)} disabled={form.carouselCard.length <= 2}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>

            <div className='Column w-100' style={{ rowGap: 12 }}>
              {/* NOTE: If you want per-card media, switch mode="carousel" and bind to card fields.
                  Keeping as-is per your current structure. */}
              <MediaPicker
                mode="carousel"
                cardIndex={cIdx}
                mediaUrl={card.mediaUrl}
                thumbnailUrl={card.thumbnailUrl}
                mediaMode={card.mediaMode}
                mediaType={card.mediaType}
                onMediaUrlChange={(url) =>
                  update(["carouselCard", cIdx, "mediaUrl"], url)
                }

                onThumbnailUrlChange={(url) =>
                  update(["carouselCard", cIdx, "thumbnailUrl"], url)
                }

                onMediaModeChange={(mode) =>
                  update(["carouselCard", cIdx, "mediaMode"], mode)
                }

                onMediaTypeChange={(type) =>
                  update(["carouselCard", cIdx], {
                    ...form.carouselCard[cIdx],
                    mediaType: type,
                    mediaUrl: "",
                    thumbnailUrl: "",
                  })
                }
                authtoken={authtoken}
              />
              <AttrField
                tokenType='body'
                size="small" label="Card Title" placeholder="Card title"
                value={card.cardTitle}
                getNextAttrToken={getNextAttrToken}
                onChange={(e) => updateCarouselCardField(cIdx, "cardTitle", e.target.value)}
                fullWidth
              />
              <AttrField
                tokenType='body'
                size="small" label="Card Description" placeholder="Card description"
                value={card.cardDescription}
                getNextAttrToken={getNextAttrToken}
                onChange={(e) => updateCarouselCardField(cIdx, "cardDescription", e.target.value)}
                fullWidth multiline rows={2}
              />
            </div>

            {/* per-card buttons */}
            <div className='Column align-items-start  w-100' style={{ marginTop: 12 }}>
              <HeaderWithInfo label="Buttons" info="Optional" />
              <FormControl size="small" sx={{ width: 200 }}>
                <Select
                  size="small"
                  displayEmpty
                  value={carouselPicker[cIdx] || ""}
                  onChange={onCarouselPick(cIdx)}
                  renderValue={(v) => v ? TYPE_LABELS[v] : <span style={{ color: "#9e9e9e" }}>Add Button</span>}
                  sx={{ '& .MuiSelect-select': { py: 1 } }}
                >
                  <MenuItem value="reply">{TYPE_LABELS.reply}</MenuItem>
                  <MenuItem value="dialer_action">{TYPE_LABELS.dialer_action}</MenuItem>
                  <MenuItem value="url_action">{TYPE_LABELS.url_action}</MenuItem>
                </Select>
              </FormControl>

              <Stack spacing={2} style={{ width: '100%', marginTop: 12 }}>
                {(card.suggestions || []).map((s, bIdx) => {
                  const type = s.suggestionType;
                  return (
                    <Stack key={bIdx} direction="column" spacing={1} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1, width: '100%' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" style={{ borderBottom: `1px solid ${colors.borderwhite}`, paddingBlockEnd: 10 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{TYPE_LABELS[type] || type}</Typography>
                        <IconButton size="small" onClick={() => removeCarouselButton(cIdx, bIdx)}><DeleteIcon fontSize="small" /></IconButton>
                      </Stack>

                      {type === "reply" && (
                        <div className='w-100 py-2 Row' style={{ columnGap: 12 }}>
                          <TextField
                            size="small"
                            style={{ width: '50%' }}
                            label="Button Text"
                            placeholder="Click to Win"
                            value={s.displayText || ""}
                            onChange={(e) => {
                              const displayText = e.target.value;
                              updateCarouselButton(cIdx, bIdx, { displayText, postback: underscore(displayText) }); // Changed this line
                            }}
                          />
                        </div>
                      )}

                      {type === "dialer_action" && (
                        <div className='w-100 py-2 Row' style={{ columnGap: 12 }}>
                          <TextField
                            size="small"
                            style={{ width: '50%' }}
                            label="Button Text"
                            placeholder="Click to Win"
                            value={s.displayText || ""}
                            onChange={(e) => {
                              const displayText = e.target.value;
                              updateCarouselButton(cIdx, bIdx, { displayText, postback: underscore(displayText) }); // Changed this line
                            }}
                          />
                          <TextField
                            size="small" style={{ width: '50%' }}
                            label="Phone Number" placeholder="+919876543210"
                            value={s.phoneNumber || ""}
                            onChange={(e) => updateCarouselButton(cIdx, bIdx, { phoneNumber: e.target.value })}
                          />
                        </div>
                      )}

                      {type === "url_action" && (
                        <div className='w-100 py-2 Row' style={{ columnGap: 12 }}>
                          <TextField
                            size="small"
                            style={{ width: '50%' }}
                            label="Button Text"
                            placeholder="Click to Win"
                            value={s.displayText || ""}
                            onChange={(e) => {
                              const displayText = e.target.value;
                              updateCarouselButton(cIdx, bIdx, { displayText, postback: underscore(displayText) });
                            }}
                          />
                          <UrlField
                            style={{ width: '50%' }}
                            size="small"
                            label="Button Link"
                            placeholder="https://example.com"
                            value={s.url || ""}
                            getNextAttrToken={getNextAttrToken}
                            onChange={(e) => updateCarouselButton(cIdx, bIdx, { url: e.target.value })}
                          />
                        </div>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </div>
          </div>
        ))}
      </Stack>
    </div>
  );

  return (
    <>
      <div className='w-100 h-100' style={{ flex: 1, overflow: 'hidden' }}>
        {/* Header */}
        <div className='Row w-100 px-4 py-4' style={{ borderBottom: `1px solid ${colors.borderwhite}`, backgroundColor: colors.white01 }}>
          <div className='Row justify-content-start pe-3' style={{ width: '30%' }}>
            <img
              src={images.BackArrowBlack}
              style={{ height: 24, width: 24, objectFit: 'contain', cursor: 'pointer', marginInlineEnd: 12 }}
              onClick={() => navigate('/creator/templates')}
            />
            <TextField
              fullWidth size='small' placeholder="Untitled Template"
              value={templateName} onChange={(e) => setTemplateName(e.target.value)}
            />
          </div>
          <div className='Row justify-content-start ps-3' style={{ columnGap: 12 }}>
            <CheerioButton
              btnText={'Submit Template'}
              textStyle={{ color: colors.white01 }}
              borderStyle={{ border: `1px solid ${colors.primary03}` }}
              backColor={colors.primary03}
              onclick={submitTemplateAction}
            />
          </div>
        </div>

        {/* Body */}
        <div className='Row w-100' style={{ height: 'calc( 100% - 90px )', flex: 1, overflow: 'hidden' }}>
          <div className='Column h-100 justify-content-start' style={{ width: '65%', overflowY: 'auto', paddingBlockStart: 10, paddingBlockEnd: 30, paddingInline: 20 }}>
            <div className='Column justify-content-start align-items-start w-100' style={{
              padding: 20,
              marginBlock: 10,
              borderRadius: 16,
              border: `1px solid ${colors.borderwhite}`,
              backgroundColor: colors.white01
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Template Variables Guide
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                Use dynamic variables to personalize your messages:
              </Typography>
              <Box sx={{ ml: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>Text variables:</strong> [1], [2], [3]... for names, dates, amounts, etc.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>URL variables:</strong> [url1], [url2]... for dynamic links in buttons
                </Typography>
                {/* <Typography variant="body2" sx={{ mb: 1 }}>
      • <strong>Image variables:</strong> [img1], [img2]... for dynamic media (dynamic mode only)
    </Typography> */}
              </Box>
              <Typography variant="caption" sx={{ color: "#888", mt: 1 }}>
                Click "+ Attr" buttons to automatically insert the next available variable token.
              </Typography>
            </div>

            {/* Type picker */}
            <div className='Column justify-content-start align-items-start w-100' style={{ padding: 20, marginBlock: 10, borderRadius: 16, border: `1px solid ${colors.borderwhite}`, backgroundColor: colors.white01 }}>
              <HeaderWithInfo label="Select template type" info="Text, Rich Card, or Carousel" />
              <FormControl>
                <Select
                  size='small' className='my-2' displayEmpty
                  value={form.type} onChange={handleChange("type")}
                  renderValue={(v) => v ? v : <span style={{ color: "#9e9e9e" }}>Select template type</span>}
                  style={{ width: '200px' }}
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="richcard">Rich Card</MenuItem>
                  <MenuItem value="carousel">Carousel</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* TEXT editor */}
            {form.type === "text" && (
              <>
                <div className='Column justify-content-start align-items-start w-100' style={{ padding: 20, marginBlock: 10, borderRadius: 16, border: `1px solid ${colors.borderwhite}`, backgroundColor: colors.white01 }}>
                  <HeaderWithInfo label="Text Message" info="Enter your message (max 1024 characters)" />
                  <AttrField
                    tokenType='body'
                    size="small" className="my-2" fullWidth multiline rows={4}
                    placeholder="Type your message here..."
                    value={form.textMessage}
                    getNextAttrToken={getNextAttrToken}
                    onChange={handleChange("textMessage")}
                    inputProps={{ maxLength: 1024 }}
                  />
                </div>
                {renderTextButtonsEditor()}
              </>
            )}

            {/* RICH CARD editor */}
            {form.type === "richcard" && renderRichCardEditor()}

            {/* CAROUSEL editor */}
            {form.type === "carousel" && renderCarouselEditor()}
          </div>

          {/* Right column: live preview */}
          <div className='Column h-100 justify-content-start' style={{ flex: 1, width: '35%', overflowY: 'auto', paddingBlockStart: 20, paddingBlockEnd: 30, paddingInline: 40 }}>
            <MessagePreview form={form} />
          </div>
        </div>
      </div>
    </>
  );
};

export default RCSTemplateCreation